import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Star as StarIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Handyman as HandymanIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SectionContainer from '../components/SectionContainer';
import { useLanguage } from '../i18n/LanguageContext';

const About = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const values = [
    { icon: <StarIcon color="secondary" />, key: 'excellence' },
    { icon: <SecurityIcon color="secondary" />, key: 'trust' },
    { icon: <SpeedIcon color="secondary" />, key: 'reliability' },
    { icon: <HandymanIcon color="secondary" />, key: 'expertise' },
  ];

  const timeline = [
    { year: '2020', key: 'founded' },
    { year: '2021', key: 'service247' },
    { year: '2022', key: 'smartHome' },
    { year: '2024', key: 'premium' },
  ];

  const reasons = [
    { icon: <SecurityIcon color="primary" />, key: 'trusted' },
    { icon: <SpeedIcon color="primary" />, key: 'emergency' },
    { icon: <StarIcon color="primary" />, key: 'pricing' },
    { icon: <HandymanIcon color="primary" />, key: 'craftsmen' },
    { icon: <SchoolIcon color="primary" />, key: 'training' },
  ];

  return (
    <Box>
      <SectionContainer backgroundColor="grey.50" py={6}>
        <Typography variant="h1" textAlign="center" gutterBottom sx={{ mb: 2 }}>
          {t('about.title')}
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          {t('about.subtitle')}
        </Typography>
      </SectionContainer>

      <SectionContainer>
        <Grid container spacing={6}>
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src="/images/chato_hs_logo.png"
              alt="Chato Home Services Logo"
              sx={{ width: '100%', height: 'auto', borderRadius: 2, boxShadow: 3, mb: 2, maxHeight: 300, objectFit: 'contain' }}
            />
            <Box
              component="img"
              src="/images/owner-placeholder.jpg"
              alt={t('about.ownerAlt')}
              sx={{ width: '100%', height: 'auto', borderRadius: 2, boxShadow: 3 }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
              {t('about.headingCompany')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              {t('about.body1')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              {t('about.body2')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              {t('about.body3')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
              {t('about.body4')}
            </Typography>
          </Grid>
        </Grid>
      </SectionContainer>

      <SectionContainer backgroundColor="grey.50">
        <Typography variant="h3" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 600 }}>
          {t('about.ourValues')}
        </Typography>
        <Grid container spacing={4}>
          {values.map((value) => (
            <Grid item xs={12} sm={6} md={3} key={value.key}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ mb: 2 }}>{value.icon}</Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t(`about.values.${value.key}.title`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`about.values.${value.key}.description`)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </SectionContainer>

      <SectionContainer>
        <Typography variant="h3" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 600 }}>
          {t('about.ourJourney')}
        </Typography>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Timeline position="alternate">
            {timeline.map((item, index) => (
              <TimelineItem key={item.key}>
                <TimelineSeparator>
                  <TimelineDot color="primary" variant="filled">
                    {index === 0 && <BusinessIcon />}
                    {index === timeline.length - 1 && <StarIcon />}
                  </TimelineDot>
                  {index < timeline.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {item.year}
                    </Typography>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {t(`about.timeline.${item.key}.title`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`about.timeline.${item.key}.description`)}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Box>
      </SectionContainer>

      <SectionContainer backgroundColor="grey.50">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
              {t('about.whyChoose')}
            </Typography>
            <List>
              {reasons.map((reason) => (
                <ListItem key={reason.key}>
                  <ListItemIcon>{reason.icon}</ListItemIcon>
                  <ListItemText
                    primary={t(`about.reasons.${reason.key}.primary`)}
                    secondary={t(`about.reasons.${reason.key}.secondary`)}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/images/central-coast-fields.jpg"
              alt={t('about.teamAlt')}
              sx={{ width: '100%', height: 'auto', borderRadius: 2, boxShadow: 3 }}
            />
          </Grid>
        </Grid>
      </SectionContainer>

      <SectionContainer backgroundColor="primary.main" sx={{ color: 'white' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            {t('about.ctaTitle')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            {t('about.ctaSubtitle')}
          </Typography>
          <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/contact')} sx={{ px: 4, py: 2, fontSize: '1.1rem' }}>
            {t('about.ctaButton')}
          </Button>
        </Box>
      </SectionContainer>
    </Box>
  );
};

export default About;
